import React from "react";
import {
  query,
  where,
  collection,
  getCountFromServer,
} from "firebase/firestore";

import { db } from "configs";
import { Regions } from "constants/enums";
import { GROUPED_ROUTE_COUNTRIES } from "constants/country-codes";

import { useToast } from "../use-toast";

type Data = {
  all: number;
  available: number;
  notAvailable: number;
  notProcessed: number;
};

type Response = { country: string; data: Data };

export const useIntervalCountriesCounter = (region: string) => {
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<Response[]>([]);

  const { handleFirebaseError } = useToast();

  const getCount = React.useCallback(async () => {
    try {
      setLoading(true);
      const countryCodes =
        GROUPED_ROUTE_COUNTRIES[region as keyof typeof Regions];

      const counterData: { country: string; data: Data }[] = [];

      for (const code of countryCodes) {
        const allResponse = await getCountFromServer(
          query(
            collection(db, "Dump"),
            where("address_info.country_code", "==", code)
          )
        );

        const availableResponse = await getCountFromServer(
          query(
            collection(db, "Dump"),
            where("address_info.country_code", "==", code),
            where("routeStatus", "==", "Available")
          )
        );

        const notAvailableResponse = await getCountFromServer(
          query(
            collection(db, "Dump"),
            where("address_info.country_code", "==", code),
            where("routeStatus", "==", "NotAvailable")
          )
        );

        const [allCount, availableCount, notAvailableCount] = [
          allResponse.data().count,
          availableResponse.data().count,
          notAvailableResponse.data().count,
        ];
        const notProccessedCount =
          allCount - (availableCount + notAvailableCount);

        counterData.push({
          country: code,
          data: {
            all: allCount,
            available: availableCount,
            notAvailable: notAvailableCount,
            notProcessed: notProccessedCount,
          },
        });
      }

      setData([...counterData]);
    } catch (error) {
      handleFirebaseError(error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region]);

  React.useEffect(() => {
    void getCount();

    const interval = setInterval(async () => await getCount(), 240000);
    return () => clearInterval(interval);
  }, [getCount]);

  return { loading, data } as const;
};
